/**
 * Resume Studio — shared UI atoms used across all three flows.
 *
 * Kept in one file because each atom is tiny (< 25 lines) and pulling
 * them into separate files would create more import lines than saved
 * body lines.
 */
import React from "react";
import { ArrowRight, Trash } from "@phosphor-icons/react";

export function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-slate-600">{label}</span>
            <div className="mt-1">{children}</div>
        </label>
    );
}

export function BackButton({ onClick }) {
    return (
        <button
            onClick={onClick}
            data-testid="resume-studio-back-btn"
            className="text-sm text-slate-500 hover:text-slate-800 mb-4 inline-flex items-center gap-1"
        >
            ← Back
        </button>
    );
}

export function BackStepButton({ onClick }) {
    return (
        <button
            onClick={onClick}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-semibold hover:bg-slate-50"
        >
            Back
        </button>
    );
}

export function NextButton({ onClick, disabled, testid }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-cobalt-600 text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-40 hover:bg-cobalt-700 mt-6"
        >
            Continue <ArrowRight size={16} />
        </button>
    );
}

export function StepDots({ current, total }) {
    return (
        <div className="flex items-center gap-2 mb-4">
            {Array.from({ length: total }, (_, i) => (
                <span
                    key={i}
                    className={`h-1.5 w-8 rounded-full transition-colors ${
                        i < current ? "bg-cobalt-600" : "bg-slate-200"
                    }`}
                />
            ))}
            <span className="text-xs text-slate-500 ml-2">
                Step {current} of {total}
            </span>
        </div>
    );
}

export function AtsScoreBadge({ score, band, label, muted }) {
    const color =
        band === "green"
            ? "text-emerald-600 bg-emerald-50 ring-emerald-500/30"
            : band === "amber"
                ? "text-amber-600 bg-amber-50 ring-amber-500/30"
                : "text-rose-600 bg-rose-50 ring-rose-500/30";
    return (
        <div className={`inline-flex flex-col items-center px-3 py-1.5 rounded-xl ring-1 ${muted ? "opacity-60" : ""} ${color}`}>
            <span className="text-[10px] uppercase tracking-wide font-bold">{label}</span>
            <span className="font-black text-lg leading-none">{score}</span>
            <span className="text-[10px]">ATS score</span>
        </div>
    );
}

// Shared card wrapper for multi-entry rows (project, internship, experience).
export function ItemCard({ label, onRemove, testid, children }) {
    return (
        <div className="mt-3 border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500">{label}</span>
                {onRemove && (
                    <button
                        onClick={onRemove}
                        data-testid={testid}
                        className="text-slate-400 hover:text-red-600"
                        aria-label="Remove"
                    >
                        <Trash size={14} />
                    </button>
                )}
            </div>
            {children}
        </div>
    );
}

// Shared from-year / to-year / is-current inputs (projects, internships, experience).
export function YearRangeInputs({ testidPrefix, fromYear, toYear, isCurrent, onChange, currentLabel }) {
    const invalid =
        fromYear && toYear && /^\d+$/.test(fromYear) && /^\d+$/.test(toYear)
            && parseInt(toYear, 10) < parseInt(fromYear, 10);
    return (
        <div className="mt-2">
            <div className="grid grid-cols-2 gap-2">
                <label className="block">
                    <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">From year</span>
                    <input
                        data-testid={`${testidPrefix}-from`}
                        value={fromYear}
                        onChange={(e) => onChange("from_year", e.target.value.replace(/\D/g, "").slice(0, 4))}
                        placeholder="2024"
                        inputMode="numeric"
                        maxLength={4}
                        className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    />
                </label>
                <label className="block">
                    <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">To year</span>
                    <input
                        data-testid={`${testidPrefix}-to`}
                        value={isCurrent ? "Present" : toYear}
                        disabled={isCurrent}
                        onChange={(e) => onChange("to_year", e.target.value.replace(/\D/g, "").slice(0, 4))}
                        placeholder="2025"
                        inputMode="numeric"
                        maxLength={4}
                        className={`w-full mt-1 border rounded-lg px-3 py-2 text-sm ${
                            invalid ? "border-red-500" : "border-slate-300"
                        } ${isCurrent ? "bg-slate-100 text-slate-500" : ""}`}
                    />
                </label>
            </div>
            <label className="flex items-center gap-2 mt-2 text-xs text-slate-600">
                <input
                    type="checkbox"
                    data-testid={`${testidPrefix}-current`}
                    checked={!!isCurrent}
                    onChange={(e) => onChange("is_current", e.target.checked)}
                    className="rounded"
                />
                {currentLabel || "Currently active"}
            </label>
            {invalid && (
                <div className="mt-1 text-xs text-red-600">To year must be ≥ From year.</div>
            )}
        </div>
    );
}

// Razorpay checkout script loader.
export function loadRazorpay() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}
